/*
 * product-variant-matrix.js — helpers for high-variant products.
 *
 * Shopify's inline `{{ product | json }}` caps at 250 variants and omits
 * `option4`/`option5` keys (values remain on `variant.options[]`). Liquid
 * exposes the full matrix via `/products/{handle}?view=variants` in pages of 250.
 */
(function () {
  'use strict';

  var PER_PAGE = 250;
  /** @type {Map<string, { variants: object[], total: number, pages: number }>} */
  var variantCache = new Map();
  /** @type {Map<string, Promise<any>>} */
  var variantInflight = new Map();

  function optionValue(variant, position) {
    if (!variant || !position) return null;
    var key = 'option' + position;
    if (variant[key] != null && variant[key] !== '') return variant[key];
    var opts = variant.options;
    if (Array.isArray(opts) && opts.length >= position) {
      var val = opts[position - 1];
      return val != null && val !== '' ? val : null;
    }
    return null;
  }

  function normalizeVariant(variant, optionCount) {
    var out = Object.assign({}, variant);
    var count = optionCount || (Array.isArray(out.options) ? out.options.length : 0);
    for (var i = 1; i <= count; i++) {
      var val = optionValue(out, i);
      if (val != null) out['option' + i] = val;
    }
    return out;
  }

  function normalizeAll(variants, optionCount) {
    return (variants || []).map(function (v) {
      return normalizeVariant(v, optionCount);
    });
  }

  function hasCompoundOption(optionNames) {
    return (optionNames || []).some(function (name) {
      return name && String(name).indexOf(':') !== -1;
    });
  }

  /** True when inline product JSON is likely truncated (Shopify 250 cap). */
  function isEmbeddedCap(product) {
    return ((product && product.variants) || []).length >= 250;
  }

  /** True when paginated ?view=variants is required (compound axes beyond JSON cap). */
  function needsFullMatrix(product) {
    if (!product) return false;
    var optionNames = product.options || [];
    if (!hasCompoundOption(optionNames)) return isEmbeddedCap(product);

    var variants = product.variants || [];
    var optionCount = optionNames.length;
    if (variants.length >= 250) return true;

    if (!variants.length) return true;
    var sample = variants.find(function (v) {
      return v.available;
    });
    if (!sample) sample = variants[0];
    for (var i = 4; i <= optionCount; i++) {
      if (!optionValue(sample, i)) return true;
    }
    return false;
  }

  /**
   * Fetch all pages for a paginated `?view=` endpoint.
   * Returns payloads in order; callers aggregate by shape (variants vs map).
   */
  async function fetchAllPages(handle, view) {
    var pageUrl = function (page) {
      return '/products/' + encodeURIComponent(handle) + '?view=' + view + '&page=' + page;
    };
    var first = await fetch(pageUrl(1)).then(function (r) {
      if (!r.ok) throw new Error(view + ' fetch failed');
      return r.json();
    });
    var payloads = [first];
    var pages = first.pages || 1;
    if (pages > 1) {
      var requests = [];
      for (var page = 2; page <= pages; page++) {
        requests.push(fetch(pageUrl(page)).then(function (r) { return r.json(); }));
      }
      payloads = payloads.concat(await Promise.all(requests));
    }
    return payloads;
  }

  async function loadAll(handle, options) {
    if (!handle) return { variants: [], total: 0, pages: 0 };

    var cached = variantCache.get(handle);
    if (cached && !(options && options.force)) return cached;

    var inflight = variantInflight.get(handle);
    if (inflight && !(options && options.force)) return inflight;

    var promise = (async function () {
      var payloads = await fetchAllPages(handle, 'variants');
      var all = [];
      payloads.forEach(function (payload) {
        if (payload && payload.variants) all = all.concat(payload.variants);
      });
      var result = {
        variants: all,
        total: payloads[0].total || all.length,
        pages: payloads[0].pages || 1,
      };
      variantCache.set(handle, result);
      return result;
    })();

    variantInflight.set(handle, promise);
    try {
      return await promise;
    } finally {
      variantInflight.delete(handle);
    }
  }

  function mergeMaps(base, extra) {
    return Object.assign({}, base || {}, extra || {});
  }

  /* loadAllIjewelMap() used to paginate `?view=ijewel-map` to build a full
     variant → GLB map. Removed: every page of that endpoint resolved a fresh
     set of `metaobjects.cad_model` handles and Shopify caps that at ~20 DISTINCT
     entries per request, so the "full" map it produced was silently incomplete
     on exactly the products that needed it (Georgian Pinky Ring: 50 distinct
     CADs, 20 returned). GLB urls are now resolved one CAD at a time by
     assets/product-ijewel-cad-resolver.js, which has no such ceiling. The
     variant MATRIX (`?view=variants`, loadAll below) is unrelated and stays. */

  /** Warm the variant matrix cache (idle / hover prefetch). */
  function prefetch(handle, product) {
    if (!handle) return Promise.resolve();
    var tasks = [];
    if (!product || needsFullMatrix(product)) {
      tasks.push(loadAll(handle).catch(function () {}));
    }
    return Promise.all(tasks);
  }

  window.ProductVariantMatrix = {
    PER_PAGE: PER_PAGE,
    optionValue: optionValue,
    normalizeVariant: normalizeVariant,
    normalizeAll: normalizeAll,
    needsFullMatrix: needsFullMatrix,
    isEmbeddedCap: isEmbeddedCap,
    hasCompoundOption: hasCompoundOption,
    loadAll: loadAll,
    mergeMaps: mergeMaps,
    prefetch: prefetch,
  };
})();
